/**
 * Fetching and opening a shared photograph, on a page with no account.
 *
 * The main media cache cannot be used here and should not be: every path
 * through it starts by asking the keyring for a group key, which is exactly
 * what a visitor following a link does not have. This is the same three steps
 * — fetch the ciphertext, unwrap the file key, decrypt — with the link key
 * standing in for the group key, and no keyring involved at all.
 *
 * Object URLs are tracked so a page that shows forty photographs can revoke
 * forty blobs when it leaves, rather than holding them until the tab closes.
 */

import { decrypt } from "@fc/core";
import { openWithLink } from "./linkKey.js";

/**
 * Turn a signed URL plus a sealed key into something an `<img>` can show.
 *
 * A file with no sealed key was never encrypted — its URL is already
 * renderable, and it is returned unchanged so callers need no branch.
 */
export async function openSharedMedia(
  url: string,
  sealedFileKey: string | null,
  linkKey: Uint8Array | null,
  contentType = "image/webp",
): Promise<string> {
  if (!sealedFileKey) return url;

  if (!linkKey) {
    throw new Error(
      "This link is missing its key. It was probably shortened or " +
        "forwarded by something that dropped the part after the #.",
    );
  }

  const fileKey = await openWithLink(sealedFileKey, linkKey);

  /*
   * No credentials: this goes to object storage, not to the API. There is no
   * session here to send anyway, which is the whole point of a share link.
   */
  const response = await fetch(url, { credentials: "omit" });
  if (!response.ok) {
    throw new Error(`The photo could not be fetched (${String(response.status)}).`);
  }

  const plaintext = await decrypt(
    new Uint8Array(await response.arrayBuffer()),
    fileKey,
  );

  return URL.createObjectURL(
    new Blob([plaintext as unknown as BlobPart], { type: contentType }),
  );
}

/**
 * Let go of everything this page made.
 *
 * Only blob URLs: a plain signed URL from the unencrypted path is not ours to
 * revoke, and `revokeObjectURL` on one is a silent no-op that would hide the
 * mistake rather than surface it.
 */
export function releaseSharedMedia(urls: Iterable<string>): void {
  for (const url of urls) {
    if (url.startsWith("blob:")) URL.revokeObjectURL(url);
  }
}
